// Vite uses import.meta.env instead of process.env
const CACHE_PREFIX = `truss_ts_${import.meta.env.VITE_ENV || 'dev'}_`;
const CACHE_EXPIRY_DAYS = 1;

interface CacheEntry<T> {
    data: T;
    timestamp: string;
    lastUpdated: string;
}

export const getCachedData = async <T>(
    key: string,
    fetchFn: () => Promise<T>,
    forceRefresh: boolean = false
): Promise<T> => {
    const cacheKey = `${CACHE_PREFIX}${key}`;
    
    // Check if we have cached data
    const cached = localStorage.getItem(cacheKey);
    
    if (cached && !forceRefresh) {
        const { data, timestamp } = JSON.parse(cached) as CacheEntry<T>;
        
        // Check if cache is still valid
        const cacheDate = new Date(timestamp);
        const now = new Date();
        const daysOld = (now.getTime() - cacheDate.getTime()) / (1000 * 60 * 60 * 24);
        
        if (daysOld < CACHE_EXPIRY_DAYS) {
            return data;
        }
    }
    
    // Fetch fresh data
    const data = await fetchFn();
    
    // Cache the new data
    const cacheEntry: CacheEntry<T> = {
        data,
        timestamp: new Date().toISOString(),
        lastUpdated: new Date().toISOString()
    };
    
    localStorage.setItem(cacheKey, JSON.stringify(cacheEntry));
    
    return data;
};

export const clearCache = () => {
    // Clear all time series caches
    const keys = [
        'category_time_series',
        'region_time_series',
        'industry_time_series',
        'source_time_series',
        'author_time_series',
        'tag_time_series'
    ];
    
    keys.forEach(key => {
        localStorage.removeItem(`${CACHE_PREFIX}${key}`);
    });
};

export const getLastUpdated = (seriesName: string): Date | null => {
    try {
        const cached = localStorage.getItem(`${CACHE_PREFIX}${seriesName}`);
        if (!cached) return null;
        
        const { lastUpdated } = JSON.parse(cached);
        return new Date(lastUpdated);
    } catch (error) {
        console.error(`Error getting last updated time for ${seriesName}:`, error);
        return null;
    }
}; 